import {
  agencyRoutes,
  demoRoutes,
  echoRoutes,
  settingsRoutes,
  ssoRoutes,
} from './groups';
import { generatePath } from './router.helpers';
import { Routes, SIGNUP_ROUTES, SwitchRoutes } from './router.types';

export const switchRoutes: SwitchRoutes = {
  agency: '[agency]',
  connectionDashboard: '/connection-dashboard',
  connectionDashboardEntry: '/connection-dashboard/[token]',
  connectionDashboardLogin: '/connection-dashboard/[token]/login',
  connectionDashboardSignup: '/connection-dashboard/[token]/create-password',
  contentSearch: '/content-search',
  creatorBrandDeals: '/creator/brand-deals',
  creatorCampaignReportList: '/campaign-report',
  creatorLiveContent: '/live-content',
  creatorLiveContentId: '/live-content/[id]',
  creatorliveContentIdTikTok: `/live-content/tiktok/[id]`,
  creatorliveContentIdYouTube: `/live-content/youtube/[id]`,
  creatorMediaPackList: '/media-packs',
  emailAssistant: '/email-assistant',
  emailAssistantResult: '/email-assistant/result',
  extensionAuthCallback: '/oauth/extension',
  extensionAuthCallbackToTutorial: '/oauth/extension/tutorial',
  foamAssistAuthCallback: '/oauth/foam-assist',
  foamAssistTutorial: '/oauth/foam-assist/tutorial',
  foamAi: '/foam-ai',
  globalSearch: '/search',
  logout: '/logout',
  managerRoster: '/roster',
  mediaPackChooseTemplate: '/media-packs/choose-template',
  mediaPackDetail: '/media-packs/[id]',
  mediaPacks: '/media-packs',
  mediaPackSharedView: '/media-packs/[id]/share',
  myAgency: '/my-agency',
  myDaily: '/my-daily',
  myRoster: '/my-roster',
  notFound: '/404',
  unauthorized: '/401',
  profileOverview: '/t/[creatorSlug]',
  watchlist: '/watchlist',
  sharedProfileOverview: '/shared/talent/[creatorSlug]',
  platformConnections: '/platform-connections',
  privacyPolicy: '/privacy',
  profileSettings: '/profile-settings',
  reconnectPlatforms: '/reconnect-platforms',
  root: '/',
  savedLists: '/saved-lists',
  savedListDetail: '/saved-lists/[id]',
  sharedMediaPack: '/shared/media-packs/[sharedToken]',
  sharedMediaPackLiveContent:
    '/shared/media-packs/[sharedToken]/live-content/[sid]',
  signUpConnectionSuccess: SIGNUP_ROUTES.signUpConnectionSuccess,
  signUpConnectSocials: SIGNUP_ROUTES.signUpConnectSocials,
  termsAndConditions: '/terms',
  slackCallback: `${process.env.NEXT_PUBLIC_SLACK_HOST ?? ''}/oauth/callback`,
  slackAuthCallback: '/oauth/slack',
  talentProfilePlatformConnections: '/t/[creatorSlug]/platform-connections',
  talentProfileTalentNotes: '/t/[creatorSlug]/talent-notes',
  talentProfileMediaKits: '/t/[creatorSlug]/media-kits',
  onboardingTalentRep: '/welcome/[step]',
  mediaKits: '/media-kits',
  mediaKitDetail: '/media-kits/[id]/builder',
  mediaKitSharedPage: '/shared/media-kits/[id]',
  mmvu: '/mmvu',
  ...echoRoutes,
  ...settingsRoutes,
  ...ssoRoutes,
  ...demoRoutes,
  ...agencyRoutes,
};

export const routes: Routes = {
  ...switchRoutes,
  connectionDashboardEntry: (token) =>
    generatePath(switchRoutes.connectionDashboardEntry, {
      params: { token },
    }),
  connectionDashboardLogin: (token) =>
    generatePath(switchRoutes.connectionDashboardLogin, {
      params: { token },
    }),
  connectionDashboardSignup: (token) =>
    generatePath(switchRoutes.connectionDashboardSignup, {
      params: { token },
    }),

  mediaPackDetail: (id: string, firstTime = false) =>
    generatePath(switchRoutes.mediaPackDetail, {
      params: { id },
      query: { firstTime },
    }),
  mediaPackShareView: (id: string) =>
    generatePath(switchRoutes.mediaPackSharedView, {
      params: { id },
    }),
  mediaKitBuilder: (id: string) =>
    generatePath(switchRoutes.mediaKitDetail, {
      params: { id },
    }),
  mediaKitShared: (id: string) =>
    generatePath(switchRoutes.mediaKitSharedPage, {
      params: { id },
    }),
  creatorLiveContentId: (id: string) =>
    generatePath(switchRoutes.creatorLiveContentId, {
      params: { id },
    }),
  creatorliveContentIdTikTok: (contentId: string) =>
    generatePath(switchRoutes.creatorliveContentIdTikTok, {
      params: { id: contentId },
    }),
  creatorliveContentIdYouTube: (contentId: string) =>
    generatePath(switchRoutes.creatorliveContentIdYouTube, {
      params: { id: contentId },
    }),
  profileOverview: (creatorSlug: string) =>
    generatePath(switchRoutes.profileOverview, {
      params: { creatorSlug },
    }),
  sharedProfileOverview: (creatorSlug: string) =>
    generatePath(switchRoutes.sharedProfileOverview, {
      params: { creatorSlug },
    }),
  sharedMediaPack: (sharedToken: string) =>
    generatePath(switchRoutes.sharedMediaPack, {
      params: { sharedToken },
    }),
  sharedMediaPackLiveContent: (sharedToken: string, sid: string) =>
    generatePath(switchRoutes.sharedMediaPackLiveContent, {
      params: { sharedToken, sid },
    }),
  slackCallback: (
    accessToken: string,
    refreshToken: string,
    userId: string,
    state: string,
  ) =>
    generatePath(switchRoutes.slackCallback, {
      query: { userId, state, accessToken, refreshToken },
    }),
  slackAuthCallback: (userId: string, state: string) =>
    generatePath(switchRoutes.slackAuthCallback, {
      query: { userId, state },
    }),
  savedListDetail: (id: string, goBackUri?: string) =>
    generatePath(switchRoutes.savedListDetail, {
      params: { id },
      query: { goBackUri: goBackUri && encodeURIComponent(goBackUri) },
    }),
  contentSearchDetailModal: (id: string) =>
    `${routes.contentSearch}?contentId=${id}`,
  talentProfilePlatformConnections: (slug: string) =>
    generatePath(switchRoutes.talentProfilePlatformConnections, {
      params: { creatorSlug: slug },
    }),
  talentProfileTalentNotes: (slug: string) =>
    generatePath(switchRoutes.talentProfileTalentNotes, {
      params: { creatorSlug: slug },
    }),
  foamIOCreatorProfile: (slug: string) =>
    `https://${slug}.${process.env.NEXT_PUBLIC_SHARED_PROFILES_HOST}`,
};
